import React, { forwardRef, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { getTableContainerWidth } from '@atlaskit/editor-common/node-width';
import { calcTableWidth } from '@atlaskit/editor-common/styles';
import { akEditorDefaultLayoutWidth, akEditorMobileBreakoutPoint } from '@atlaskit/editor-shared-styles';
import { TABLE_MAX_WIDTH } from '../pm-plugins/table-resizing/utils';
import { TableCssClassName as ClassName } from '../types';
import { TableResizer } from './TableResizer';
var getMarginLeft = function getMarginLeft(lineLength, tableWidth) {
  var marginLeft;
  if (tableWidth !== 'inherit' && lineLength) {
    var containerWidth = tableWidth;
    if (containerWidth) {
      marginLeft = (lineLength - containerWidth) / 2;
    }
  }
  return marginLeft;
};
export var InnerContainer = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    style = _ref.style,
    node = _ref.node,
    children = _ref.children;
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    style: style,
    className: className,
    "data-number-column": node.attrs.isNumberColumnEnabled,
    "data-layout": node.attrs.layout,
    "data-test-id": "table-container"
  }, children);
});
export var ResizableTableContainer = function ResizableTableContainer(_ref2) {
  var children = _ref2.children,
    className = _ref2.className,
    node = _ref2.node,
    lineLength = _ref2.lineLength,
    containerWidth = _ref2.containerWidth,
    editorView = _ref2.editorView,
    getPos = _ref2.getPos,
    tableRef = _ref2.tableRef,
    isResizing = _ref2.isResizing,
    pluginInjectionApi = _ref2.pluginInjectionApi;
  var containerRef = useRef(null);
  var marginLeftRef = useRef(0);
  var tableWidthRef = useRef(akEditorDefaultLayoutWidth);
  var updateWidth = useCallback(function (width) {
    if (!containerRef.current) {
      return;
    }
    var marginLeft = getMarginLeft(lineLength, width);

    // make sure during resizing
    // the pm-table-resizer-container width is the same as its child div resizer-item
    // otherwise when resize table from wider to narrower , pm-table-resizer-container stays wider
    // and cause the fabric-editor-popup-scroll-parent to overflow
    if (containerRef.current.style.width !== "".concat(width, "px")) {
      containerRef.current.style.width = "".concat(width, "px");
    }
    if (marginLeftRef.current !== marginLeft) {
      marginLeftRef.current = marginLeft;
      if (Number.isFinite(marginLeft)) {
        containerRef.current.style.marginLeft = "".concat(marginLeft, "px");
      }
    }
  }, [lineLength]);
  var displayGuideline = useCallback(function (guidelines) {
    var _pluginInjectionApi$g, _pluginInjectionApi$g2, _pluginInjectionApi$g3;
    return (_pluginInjectionApi$g = pluginInjectionApi === null || pluginInjectionApi === void 0 ? void 0 : (_pluginInjectionApi$g2 = pluginInjectionApi.guideline) === null || _pluginInjectionApi$g2 === void 0 ? void 0 : (_pluginInjectionApi$g3 = _pluginInjectionApi$g2.actions) === null || _pluginInjectionApi$g3 === void 0 ? void 0 : _pluginInjectionApi$g3.displayGuideline(editorView)({
      guidelines: guidelines
    })) !== null && _pluginInjectionApi$g !== void 0 ? _pluginInjectionApi$g : false;
  }, [pluginInjectionApi, editorView]);
  var attachAnalyticsEvent = useCallback(function (payload) {
    var _pluginInjectionApi$a;
    return pluginInjectionApi === null || pluginInjectionApi === void 0 ? void 0 : (_pluginInjectionApi$a = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a === void 0 ? void 0 : _pluginInjectionApi$a.actions.attachAnalyticsEvent(payload);
  }, [pluginInjectionApi]);
  var tableWidth = getTableContainerWidth(node);
  // 76 is currently an accepted padding value considering the spacing for resizer handle
  var responsiveContainerWidth = containerWidth - 76;
  var width = Math.min(tableWidth, responsiveContainerWidth);
  if (!isResizing) {
    tableWidthRef.current = width;
    marginLeftRef.current = getMarginLeft(lineLength, width);
  }
  var maxResizerWidth = Math.min(responsiveContainerWidth, TABLE_MAX_WIDTH);
  return /*#__PURE__*/React.createElement("div", {
    style: {
      marginLeft: marginLeftRef.current,
      width: tableWidthRef.current
    },
    className: ClassName.TABLE_RESIZER_CONTAINER,
    ref: containerRef
  }, /*#__PURE__*/React.createElement(TableResizer, {
    width: width,
    maxWidth: maxResizerWidth,
    containerWidth: containerWidth,
    updateWidth: updateWidth,
    editorView: editorView,
    getPos: getPos,
    node: node,
    tableRef: tableRef,
    displayGuideline: displayGuideline,
    attachAnalyticsEvent: attachAnalyticsEvent
  }, /*#__PURE__*/React.createElement(InnerContainer, {
    className: className,
    node: node
  }, children)));
};
export var TableContainer = function TableContainer(_ref3) {
  var children = _ref3.children,
    node = _ref3.node,
    className = _ref3.className,
    _ref3$containerWidth = _ref3.containerWidth,
    lineLength = _ref3$containerWidth.lineLength,
    editorWidth = _ref3$containerWidth.width,
    isTableResizingEnabled = _ref3.isTableResizingEnabled,
    isBreakoutEnabled = _ref3.isBreakoutEnabled,
    editorView = _ref3.editorView,
    getPos = _ref3.getPos,
    tableRef = _ref3.tableRef,
    isNested = _ref3.isNested,
    isResizing = _ref3.isResizing,
    pluginInjectionApi = _ref3.pluginInjectionApi;
  if (isTableResizingEnabled && !isNested) {
    return /*#__PURE__*/React.createElement(ResizableTableContainer, {
      className: className,
      node: node,
      lineLength: lineLength,
      containerWidth: editorWidth,
      editorView: editorView,
      getPos: getPos,
      tableRef: tableRef,
      isResizing: isResizing,
      pluginInjectionApi: pluginInjectionApi
    }, children);
  }
  var tableWidth = isBreakoutEnabled ? calcTableWidth(node.attrs.layout, editorWidth) : 'inherit';
  return /*#__PURE__*/React.createElement(InnerContainer, {
    node: node,
    className: classNames(className, {
      'less-padding': editorWidth < akEditorMobileBreakoutPoint && !isNested
    }),
    style: {
      width: tableWidth,
      marginLeft: getMarginLeft(lineLength, tableWidth)
    }
  }, children);
};